export class FacetItem {
    term: string;
    count: number;

    /**
     * Formatted count
     */
    _count?: string;
}

export class SubFilter {
    value: string;
    label?: string;
    widgetId: string;
    widgetLabel?: string;
}

export interface ISeria {
    series_id: string;
    fields?: { [key: string]: any; };
    points?: number[][];
    highlight?: { [key: string]: any; };
    getFieldHtmlWithHighlight?(field: string, separator?: string): string;
}

export interface IDataResponse {
    facets?: any; // TODO: define type
    series: ISeria[];
    total: number;
    success: true;
    suggest: {
        value: string,
        highlighted: string
    }[]
}

export interface IWSChangeResponse {
    "topic": "seriesupdates",
    "query": string,
    "details": {
        "series_id": string
    }
}

/**
 * WebSockets service
 */
export interface IWSService {
    /**
     * Subscribe to topic query change. Returns unsubscribe method
     */
    subscribe(topic: string, query: string, callback: (data: IWSChangeResponse) => void): () => void;
    /**
     * Close connection
     */
    close(): void;
}

export interface IChartsDataResponse extends IDataResponse {
    hits: ISeria[];
}

export interface RequestOptions {
    ignore_errors?: boolean | { [key: string]: true };
    format_error?: (response) => string | false;
}

export interface ISjClient {
    request(url, method, args, data, success?, error?, options?, requestOptions?: RequestOptions);
    raw: {
        get(method_url: string, args: { [key: string]: any }, success?, error?, requestOptions?: RequestOptions);
        post(method_url: string, url_args: { [key: string]: any }, post_data: { [key: string]: any }, success?, error?, requestOptions?: RequestOptions);
    };
    download(url: string, method: string, url_args: { [key: string]: any }, data: { [key: string]: any }, success_callback, error_callback, fileName: string | ((fname) => string));
    ws(options?): {
        /**
         * Subscribe to topic query change. Returns unsubscribe method
         */
        subscribe(topic: string, query: string, callback: (data: any) => void): () => void,
        /**
         * Close connection
         */
        close(): void
    }
}

export interface IGlobalOptions {
    initial_search?: boolean;
    filter_query?: string;
    source?: 'embed' | 'sjweb' | 'sjviews' | 'editor' | 'selector'; // to enable/disable features
    get_view_details?: () => any;

    /**
     * View theme
     */
    theme?: 'sj-theme-shooju-22';

    /*
    search_debounce?: number = 100;
    ignore_errors?: boolean;
    onInit?: (component) => void;
    onSetState?: (sjsearch: ISJSearch, state: any, widgetId?: string, skipSearch?: boolean) => void;
    default_state?: { [widgetId: string]: any };
    linker?: boolean;
    grids?: any;
    */
}

export interface IWidgetService extends IEventService {
    seriesParam: {
        sort?: string,
        max_facet_values?: number,
        per_page?: number,
        page?: number,
        default_fields?: string | ((any) => string)
    };
    getSearchParams(override: any, onlyPageParams: boolean): any;
    getGlobalOptions(): IGlobalOptions;
    g: IGlobalOptions;
    getAllWidgets(): any;
    getWidgetById(id: string): any;

    /**
     * Serialize params to url string params
     * @param params - keys/values 
     */
    serializeParams(params: any): string;

    formatQueryParts(parts: string[]): string;
    forceSetState(skipSearch?: boolean): void;
    setState(skipSearch?: boolean): void;
    addMessage(msg: {
        message: string;
        reload?: true;
        type?: 'info' | 'warning' | 'error'
    }): void;
    sjclient: ISjClient;
    appProvider: {
        options: ISJSearchSettings & ISJSearchAuthSettings
    };
    authService: ISJSearchAuth;
    encodeService: {
        htmlEncode(str: string): string;
        htmlDecode(str: string): string;
    };
    sjSearchInstance: any;
    sharedServices: any;
    stateService: {
        currentState: any;
        getSavedWidgetState(widgetId: string): any;
    },

    /**
     * string getter for options search_api_path (can be function or string)
     */
    search_api_path?: string;

    cdr: any;
    helpers: {
        /**
         * Format query part with field and values with escaping
         * e.g. `zzz=1` or `"zz z"="a b c"` or `zzz:(1,2,"a a")`
         */
        formatQueryPart(field: string, values: any[] | any, operator?: any): string;

        /**
         * Escape text for query
         */
        escapeQueryText(val): string;
    }
    waitForPage(): Promise<void>;
    waitForJQuery(): Promise<any>;
    runPipeline(name: string, data: any): Promise<void>;

    /**
     * manually subscribe to 'SUBMIT' button click if 'global.submit==true'
     */
    applyPendingChangesSubject: any;

    /**
     * ShoojuClient
     */
    shoojuClient: any;

    /**
     * Client config
     */
    getConfig(): any;

    /**
     * Returns actual query
     */
    awaitGlobalQuery(widgetComponentToIgnore?: any, includeFilterQuery?: boolean, includeOperators?: boolean, widgetsToInclude?: string[]): Promise<string>;

    /**
     * Returns actual query operators
     */
    awaitGlobalQueryOperators(widgetComponentToIgnore?: any): Promise<string>;

    /**
     * Send message to Sentry
     */
    captureMessage(message: string, level?: "info" | "warning" | "error");

    /**
     * Get all url params
     */
    getUrlParams();

    /**
     * Split fields into array (also handling expression fields
     */
    splitFields(str: string): string[];

    /**
     * Router handler (name key or implementation)
     * historystate - no url changes, state is saved to historystate
     * hash (default mode if not set) - #query=xxxx&wst=zzzz
     * url - ?query=xxxx&wst=zzzz
     */
    getRouterHandler(): "historystate" | "hash" | "url" | void | any;
}

export interface ISJSearchSettings {
    search_api_path?: string | ((s: IWidgetService) => string);
    postprocess_result?: (any) => any;
    jsresources?: string[];
    global?: {
        search_debounce: number;
    };
    series_params?: {
        max_facet_values?: number;
        per_page?: number;
        sort?: string;
        highlight?: (service: IWidgetService) => string;
        max_points?: number;
    };
    template: {
        layout: string;
    };
    styles?: string;
    widgets: any[];
}

export interface ISJSearchAuth {
    server: string;
    user?: string;
    getCredentials?: () => {
        api_secret: string,
        api_username: string,
        dynamic?: boolean // using Shooju.fn.getCredentials() (used for frame in sjchart)
    };
}

export interface ISJSearchExtra {
    onComplete?: (sjSearchInctance, destroyed: boolean) => void;
    onError?: (error) => void;

    onRequest?: (success, xhr, requestOptions, response) => void;
    saveStateOnReload?: false; // used for `invalid_credentials` api error

    /**
     * Disable route provider
     */
    ignoreRouteProvider?: true;
}

export interface ISJSearchAuthSettings {
    auth: ISJSearchAuth;
}

export interface IEventService {
    /** Emit event with name="key" from "sender" and send "data" object" */
    emit(sender: any, key: any, data?: any);

    /** Subscribes to event with name==key param */
    on(key: any, handler: (p: any, ...args) => any);

    unsubscribeAll();
}

export interface IWidget<TData, TSettings, TState> {
    data: TData;
    /**
     * Same as data
     */
    d: TData;
    settings: TSettings;
    service: IWidgetService;
    s: IWidgetService;
    state: TState;
    $: any;
    id: string;
    type: string;
    // DEPRECATED. use 'widget.service.awaitGlobalQuery(widget)'
    awaitGlobalQueryWithoutMe: () => Promise<string>;
    // DEPRECATED. use 'widget.service.awaitGlobalQuery()'
    awaitGlobalQuery: () => Promise<string>;
    events: IEventService;
    on?(key: any, handler: (p: any, ...args) => any);
    widgetDiv: {
        element: {
            nativeElement: HTMLElement;
        }
    },
    refreshGridContainerHeightIfNecessary: () => void;
    widget: IWidgetDefinition<TData, TSettings, TState>;
    definition?: {
        defaultState?: TState & { widgetName?: string }
    }
}

export class Widget<TData, TSettings, TState> implements IWidget<TData, TSettings, TState> {
    data: TData;
    d: TData;
    settings: TSettings;
    service: IWidgetService;
    s: IWidgetService;
    state: TState;
    $: any;
    id: string;
    type: string;
    // DEPRECATED. use 'widget.service.awaitGlobalQuery(widget)'
    awaitGlobalQueryWithoutMe: () => Promise<string>;
    // DEPRECATED. use 'widget.service.awaitGlobalQuery()'
    awaitGlobalQuery: () => Promise<string>;
    events: IEventService;
    on?(key: any, handler: (p: any, ...args) => any);
    widgetDiv: {
        element: {
            nativeElement: HTMLElement;
        }
    };
    refreshGridContainerHeightIfNecessary: () => void;
    widget: IWidgetDefinition<TData, TSettings, TState>;
    definition?: {
        defaultState?: TState & { widgetName?: string }
    }
}

export interface IBaseSettings {
    isActive?: (widget) => boolean;
    /**
     * Label overrides
     */
    labels?: any;
}

export interface IWidgetSettings<TSettings extends IBaseSettings, TState> {
    /**
     * Widget id. Should be some unique string.
     * @TJS-type string
     */
    id: string,

    /**
     * Type. Should be one of supported widget types.
     * @TJS-type string
    */
    type: string,

    /**
     * Default state.
    */
    defaultState?: TState,

    /**
     * Widget settings.
    */
    settings: TSettings
}

export interface IWidgetDefinition<TData, TSettings, TState> {
    init(widget: IWidget<TData, TSettings, TState>): void;
    afterInit?(widget: IWidget<TData, TSettings, TState>): Promise<void> | void;
    stateInit?(widget: IWidget<TData, TSettings, TState>): void | Promise<void>;
    getQuery?(widget: IWidget<TData, TSettings, TState>): string | void | Promise<string>;
    getQueryOperators?(widget: IWidget<TData, TSettings, TState>): string | void | Promise<string>;
    getFields?(widget: IWidget<TData, TSettings, TState>): string | string[] | Promise<string[]> | void;
    getSubFilters?(widget: IWidget<TData, TSettings, TState>): { value: string, label?: string, widgetId: string, widgetLabel?: string }[];
    removeSubFilter?(widget: IWidget<TData, TSettings, TState>, filter: { value: string, label?: string, widgetId: string, widgetLabel?: string }): void;
    getFacets?(widget: IWidget<TData, TSettings, TState>): string[] | void;
    beforeSearch?(widget: IWidget<TData, TSettings, TState>, requestargs?: any): Promise<void> | void;
    onData?(widget: IWidget<TData, TSettings, TState>, data: IDataResponse): void;
    beforeStateSet?(widget: IWidget<TData, TSettings, TState>): Promise<void> | void;
    pipeline?(widget: IWidget<TData, TSettings, TState>, name: string, data: any): void | Promise<void> | number;
    prepareForPrint?(widget: IWidget<TData, TSettings, TState>): Promise<void> | void;
    afterPrint?(widget: IWidget<TData, TSettings, TState>): Promise<string[]> | void;
    destroy?(widget: IWidget<TData, TSettings, TState>): void;

    /**
     * called before every search. if all widgets return 'false' here or has 'settings.static==true'
     * then search request will not run
     */
    shouldRunSearch?(widget: IWidget<TData, TSettings, TState>): void | boolean | Promise<boolean>;

    /**
     * called if search request skipped (see 'shouldRunSearch')
     */
    searchRejected?(widget: IWidget<TData, TSettings, TState>, requestArgs?: any): void;

    /**
     * called when widget is not active (`settings.IsActive()` returns false)
     */
    notActiveAction?(widget: IWidget<TData, TSettings, TState>, action: string): void;

    template: string;
    defaultState?: TState;
    styles?: string | ((widget: IWidget<TData, TSettings, TState>) => string);
    classNames?: string | string[];
    sendMessageToEditor?(msg: any): void;
}

export interface SJPluginSettings {
    onChanges?: (plugin: SJPlugin, changes: any) => void;
    onInit?: (plugin: SJPlugin) => void;
    doCheck?: (plugin: SJPlugin) => void;
    onDestroy?: (plugin: SJPlugin) => void;
    afterContentInit?: (plugin: SJPlugin) => void;
    afterContentChecked?: (plugin: SJPlugin) => void;
    afterViewInit?: (plugin: SJPlugin) => void;
    afterViewChecked?: (plugin: SJPlugin) => void;
}

export interface SJPlugin {
    settings: SJPluginSettings;
    $element: {
        nativeElement: any;
    }
}

export function registerWidget(name, definition, baseType?: string) {
    (<any>window).SJSearch.registerWidget(name, definition, baseType);
}

export function createSJSearch(selector: string, settings: ISJSearchSettings, authSettings?: ISJSearchAuthSettings, extra?: any) {
    var sjsearch = (<any>window).SJSearch;
    if (sjsearch && sjsearch.create) {
        sjsearch.enable_sentry(false); // prevent sentry errors
        sjsearch.create(selector, settings, authSettings, extra);
    } else {
        var element = document.createElement('script');
        element['src'] = '//cdn.shooju.com/sjsearch/loaders/alpha.js';
        document.head.appendChild(element);
        element.onload = function () {
            var sjsearchloader = (<any>window).SJSearchLoader;
            if (sjsearchloader && sjsearchloader.load) {
                sjsearchloader.load(function (SJSearch) {
                    sjsearch = (<any>window).SJSearch;
                    sjsearch.enable_sentry(false); // prevent sentry errors
                    sjsearch.create(selector, settings, authSettings, extra);
                });
            }
        }
    }
}

export function normalizeSeriesQuery(service: IWidgetService, series_query: string) {
    // remove unnecessary double quotes
    // e.g. `sid="test\1"` => `sid=test\1`

    // if sesries_query ends with double quote
    if (series_query && series_query[series_query.length - 1] == '"') {
        var parts = series_query.split('=');
        var field = parts[0];
        parts.shift();
        var qval = parts.join('=');
        if (qval[0] == '"' && qval[qval.length - 1] == '"') {
            qval = qval.substr(1, qval.length - 2);
            if (!service.shoojuClient.constants.escapeCharsExp.test(qval)) {
                // double quotes are not necessary 
                return `${field}=${qval}`;
            }
        }
    }
    return series_query;
}

export var xprMetaFields: string[] = window['SJSearch'].xprMetaFields || [];

export var metaFields: string[] = window['SJSearch'].metaFields || [];

export function convertSidToQuery(service: IWidgetService, series_id: string, field = 'sid') {
    /*
    1. if start with `$` then `substr(1)`
    2. if start with `=` than leave as is
    3. if contains special characters then replace with `'sid="'+previous+'"'`
    4. else add `sid=` to beginning
    */
    if (!series_id) {
        return series_id;
    }
    if (series_id.indexOf('$') == 0) {
        return series_id.substr(1);
    }
    if (series_id.indexOf('=') == 0 || series_id.indexOf(`${field}=`) == 0) {
        return series_id;
    }
    var ops_start = series_id.indexOf('@');
    var ops = '';
    if (ops_start != -1) {
        if (ops_start != -1) {
            ops = series_id.substr(ops_start);
            series_id = series_id.substr(0, ops_start);
        }
    }

    if (service.shoojuClient.constants.escapeCharsExp.test(series_id)) {
        return `${field}="${series_id}"${ops}`;
    } else {
        return `${field}=${series_id}${ops}`;
    }
}

export interface IUrlParamWidget {
    /**
     * Returns value from url based on urlParam setting
     */
    getUrlParam(paramName): string;

    /**
     * Return url param key
     */
    getUrlParamName(): string;

    /**
     * Return url param value from state
     */
    getUrlValue(): any;

    /**
     * Returns state without url param value
     */
    getStateWithoutUrlParam(): any;
}